import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router';
import { Clock, MoveRight, Moon, Plus, Minus, Mail, SquareArrowOutUpRight, Trash2, UserX, Ban } from 'lucide-react';
import { ConfirmToggleDialog } from '@/components/shared/confirm-toggle-dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/sonner';
import { Segmented } from '@/modules/simulator/ui/segmented';
import {
	ChipInput,
	SaveBar,
	SETTINGS_INPUT_CLASS,
	SettingsSelect,
	SETTINGS_PILL_BUTTON_CLASS,
	SettingsBody,
	SettingsCard,
	SettingsCardSkeleton,
	SettingsDivider,
	SettingsError,
	SettingsSection,
	ToggleRow
} from '@/modules/settings';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/hooks/use-permissions';
import { useAiSettings, useUpdateAiSettings, useDefaultChannels, useUpdateChannel } from '@/hooks';
import { buildDefaultAiSettings } from '@/lib/ai-settings-defaults';
import type { AiSettings, DaySchedule, TimeSlot, OperatingHours, Channel, ChannelInboxEntry, ChannelFilterMode } from '@/types';

// ============================================================================
// Constants
// ============================================================================

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
type Mode = 'custom' | 'fulltime';
const MODE_OPTIONS: { value: Mode; label: string }[] = [
	{ value: 'custom', label: 'Custom Hours' },
	{ value: 'fulltime', label: 'Full-time' }
];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_LIST_ENTRIES = 50;
const MAX_TAG_LENGTH = 50;

/** Generate 30-minute increment time options "HH:mm" */
function buildTimeOptions(): { value: string; label: string }[] {
	const opts: { value: string; label: string }[] = [];
	for (let h = 0; h < 24; h++) {
		for (const m of [0, 30]) {
			const hh = String(h).padStart(2, '0');
			const mm = String(m).padStart(2, '0');
			opts.push({ value: `${hh}:${mm}`, label: `${hh}:${mm}` });
		}
	}
	opts.push({ value: '23:59:59', label: '23:59' });
	return opts;
}
const TIME_OPTIONS = buildTimeOptions();

function buildDefaultSchedule(): DaySchedule[] {
	return ([0, 1, 2, 3, 4, 5, 6] as const).map((day) => ({
		day,
		enabled: false,
		allDay: false,
		slots: []
	}));
}

function scheduleEqual(a: DaySchedule[], b: DaySchedule[]): boolean {
	return JSON.stringify(a) === JSON.stringify(b);
}

/** Minutes since midnight for a "HH:mm" string. */
function toMinutes(time: string): number {
	const [h, m] = time.split(':').map(Number);
	return h * 60 + m;
}

/**
 * Returns the indices of slots that are invalid within a single day:
 *  - the end time is not after the start time, or
 *  - the slot overlaps another slot.
 * Slots that only touch at a boundary (e.g. 12:00–13:00 and 13:00–14:00) do NOT overlap.
 */
function findSlotConflicts(slots: TimeSlot[]): Set<number> {
	const conflicts = new Set<number>();
	slots.forEach((slot, i) => {
		if (!slot.startTime || !slot.endTime) return;
		if (toMinutes(slot.startTime) >= toMinutes(slot.endTime)) conflicts.add(i);
	});
	for (let i = 0; i < slots.length; i++) {
		for (let j = i + 1; j < slots.length; j++) {
			const a = slots[i];
			const b = slots[j];
			if (!a.startTime || !a.endTime || !b.startTime || !b.endTime) continue;
			if (toMinutes(a.startTime) < toMinutes(b.endTime) && toMinutes(b.startTime) < toMinutes(a.endTime)) {
				conflicts.add(i);
				conflicts.add(j);
			}
		}
	}
	return conflicts;
}

/** Trims, caps and dedupes a tag list like the backend does; null (with a toast) if it can't be saved. */
function cleanTagList(next: string[], others: string[]): string[] | null {
	const trimmed = next.map((t) => t.trim()).filter(Boolean);
	if (trimmed.some((t) => t.length > MAX_TAG_LENGTH)) {
		toast.error(`Tags must be 1–${MAX_TAG_LENGTH} characters.`);
		return null;
	}
	if (trimmed.length > MAX_LIST_ENTRIES) {
		toast.error(`You can add at most ${MAX_LIST_ENTRIES} tags per list.`);
		return null;
	}
	const conflict = trimmed.find((t) => others.some((o) => o.toLowerCase() === t.toLowerCase()));
	if (conflict) {
		toast.error(`"${conflict}" is already used in the other list.`);
		return null;
	}
	const seen = new Set<string>();
	return trimmed.filter((t) => {
		const key = t.toLowerCase();
		if (seen.has(key)) return false;
		seen.add(key);
		return true;
	});
}

// ============================================================================
// Sub-components
// ============================================================================

/** Inline select styled to match Figma input fields */
interface TimeSelectProps {
	value: string;
	onChange: (v: string) => void;
	placeholder?: string;
	invalid?: boolean;
}

export function TimeSelect({ value, onChange, placeholder = 'From', invalid = false }: TimeSelectProps) {
	return (
		<SettingsSelect
			value={value}
			onValueChange={onChange}
			options={TIME_OPTIONS}
			placeholder={placeholder}
			invalid={invalid}
			aria-label={placeholder}
			className={cn('min-w-0 flex-1 pl-2.5 pr-2 text-[12px]', invalid && 'border-red-400')}
		/>
	);
}

const ICON_BUTTON_CLASS =
	'flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-gray-800 transition-colors hover:bg-gray-100';
const TEXT_BUTTON_CLASS =
	'flex h-7 shrink-0 cursor-pointer items-center rounded-full px-3 text-[12px] whitespace-nowrap text-gray-900 transition-colors hover:bg-gray-100';

/** A single time slot row: From → To, with Remove; the last row also gets Add and "Set to All Day". */
interface SlotRowProps {
	slot: TimeSlot;
	isLast: boolean;
	invalid?: boolean;
	onChange: (s: TimeSlot) => void;
	onRemove: () => void;
	onAdd: () => void;
	onSetAllDay: () => void;
}

function SlotRow({ slot, isLast, invalid = false, onChange, onRemove, onAdd, onSetAllDay }: SlotRowProps) {
	return (
		<div className='flex w-full items-center gap-1.5'>
			<div className='flex flex-1 items-center gap-1.5'>
				<TimeSelect value={slot.startTime} onChange={(v) => onChange({ ...slot, startTime: v })} placeholder='From' invalid={invalid} />
				<MoveRight className='size-3 shrink-0 text-gray-800' />
				<TimeSelect value={slot.endTime} onChange={(v) => onChange({ ...slot, endTime: v })} placeholder='To' invalid={invalid} />
			</div>
			{/* Fixed width so every row's time boxes line up, with or without the last row's extra buttons. */}
			<div className='flex w-[160px] shrink-0 items-center gap-0.5'>
				<button type='button' onClick={onRemove} className={ICON_BUTTON_CLASS} aria-label='Remove slot'>
					<Minus className='size-3.5' />
				</button>
				{isLast && (
					<>
						<button type='button' onClick={onAdd} className={ICON_BUTTON_CLASS} aria-label='Add slot'>
							<Plus className='size-3.5' />
						</button>
						<div className='h-3 w-px bg-gray-500' />
						<button type='button' onClick={onSetAllDay} className={TEXT_BUTTON_CLASS}>
							Set to All Day
						</button>
					</>
				)}
			</div>
		</div>
	);
}

/** One row in the per-day operating hours list */
interface DayRowProps {
	schedule: DaySchedule;
	onChange: (s: DaySchedule) => void;
}

function DayRow({ schedule, onChange }: DayRowProps) {
	const { day, enabled, allDay, slots } = schedule;
	const isMultiLine = enabled && !allDay && slots.length > 0;

	const handleAddSlot = () => {
		onChange({ ...schedule, slots: [...slots, { startTime: '09:00', endTime: '17:00' }] });
	};

	const handleSlotChange = (idx: number, slot: TimeSlot) => {
		const next = [...slots];
		next[idx] = slot;
		onChange({ ...schedule, slots: next });
	};

	const handleSlotRemove = (idx: number) => {
		if (slots.length === 1) {
			onChange({ ...schedule, enabled: false, allDay: false, slots: [] });
		} else {
			onChange({ ...schedule, slots: slots.filter((_, i) => i !== idx) });
		}
	};

	const slotConflicts = findSlotConflicts(slots);

	return (
		<div className={cn('flex justify-between gap-3 rounded-[14px] p-1.5', isMultiLine ? 'items-start' : 'min-h-[52px] items-center')}>
			<div className={cn('flex shrink-0 items-center gap-4', isMultiLine && 'pt-2')}>
				<Switch
					checked={enabled}
					aria-label={DAY_NAMES[day]}
					onCheckedChange={(checked) => onChange({ ...schedule, enabled: checked, allDay: checked, slots: [] })}
				/>
				<span className='w-[90px] text-[14px] font-medium leading-5 text-ink'>{DAY_NAMES[day]}</span>
			</div>

			<div className='flex w-[400px] items-center gap-3 self-stretch'>
				<div className={cn('w-px shrink-0 bg-gray-500', isMultiLine ? 'self-stretch' : 'h-3')} />

				{!enabled && (
					<div className='flex h-10 items-center gap-3 px-3 text-[12px] text-gray-800'>
						<Moon className='size-4 shrink-0' />
						<span>In-Active</span>
					</div>
				)}

				{enabled && allDay && (
					<div className='flex flex-1 items-center justify-between gap-1'>
						<div className='flex h-10 w-[188px] items-center gap-2 rounded-[12px] border border-gray-500 bg-white px-3 text-[13px] text-ink'>
							<Clock className='size-3.5 shrink-0 text-gray-800' />
							<span>Active All Day</span>
						</div>
						<div className='h-3 w-px bg-gray-500' />
						<button
							type='button'
							onClick={() => onChange({ ...schedule, allDay: false, slots: [{ startTime: '09:00', endTime: '17:00' }] })}
							className={TEXT_BUTTON_CLASS}
						>
							Set Hours
						</button>
					</div>
				)}

				{enabled && !allDay && (
					<div className='flex flex-1 flex-col gap-3 py-1'>
						{slots.length === 0 && (
							<button type='button' onClick={handleAddSlot} className='h-10 w-fit cursor-pointer text-[12px] text-indigo-500 hover:underline'>
								+ Add time slot
							</button>
						)}
						{slots.map((slot, idx) => (
							<SlotRow
								key={idx}
								slot={slot}
								isLast={idx === slots.length - 1}
								invalid={slotConflicts.has(idx)}
								onChange={(s) => handleSlotChange(idx, s)}
								onRemove={() => handleSlotRemove(idx)}
								onAdd={handleAddSlot}
								onSetAllDay={() => onChange({ ...schedule, allDay: true, slots: [] })}
							/>
						))}
						{slotConflicts.size > 0 && (
							<p className='text-[12px] leading-4 text-red-500'>Time slots can&apos;t overlap, and each must end after it starts.</p>
						)}
					</div>
				)}
			</div>
		</div>
	);
}

// ============================================================================
// Ingestion › Operating Hours
// ============================================================================

export function OperatingHoursTab() {
	const { data: settings, isLoading, isError } = useAiSettings();
	const updateMutation = useUpdateAiSettings();
	const { can } = usePermissions();
	const canEdit = can('edit', 'brand');

	const [mode, setMode] = useState<Mode>('custom');
	const [schedule, setSchedule] = useState<DaySchedule[]>(buildDefaultSchedule());
	const [savedMode, setSavedMode] = useState<Mode>('custom');
	const [savedSchedule, setSavedSchedule] = useState<DaySchedule[]>(buildDefaultSchedule());

	useEffect(() => {
		const oh: OperatingHours = settings?.operatingHours ?? { mode: 'custom', timezone: '', schedule: buildDefaultSchedule() };
		const rawMode = oh.mode as string;
		const coercedMode: Mode = rawMode === 'off' || !rawMode ? 'custom' : (oh.mode as Mode);
		// Backfilled fulltime accounts store schedule: []; fall back to 7 editable rows.
		const effectiveSchedule = Array.isArray(oh.schedule) && oh.schedule.length === 7 ? oh.schedule : buildDefaultSchedule();
		setMode(coercedMode);
		setSchedule(effectiveSchedule);
		setSavedMode(coercedMode);
		setSavedSchedule(effectiveSchedule);
	}, [settings]);

	// The timezone is set on My Brand, with business hours; the schedule is read in it.
	const brandTimezone = settings?.operatingHours?.timezone || '';
	const isDirty = mode !== savedMode || !scheduleEqual(schedule, savedSchedule);

	const handleDayChange = useCallback((updated: DaySchedule) => {
		setSchedule((prev) => prev.map((d) => (d.day === updated.day ? updated : d)));
	}, []);

	const hasScheduleConflicts = mode === 'custom' && schedule.some((d) => d.enabled && !d.allDay && findSlotConflicts(d.slots).size > 0);

	const handleCancel = () => {
		setMode(savedMode);
		setSchedule(savedSchedule);
	};

	/** Writes Kim's operating schedule, keeping the brand's saved timezone and business hours. */
	const handleSave = async () => {
		if (hasScheduleConflicts) {
			toast.error('Please fix the overlapping time slots before saving.');
			return;
		}
		if (!brandTimezone) {
			toast.error('Set your brand timezone in My Brand before saving.');
			return;
		}
		const base = buildDefaultAiSettings(settings);
		try {
			await updateMutation.mutateAsync({ ...base, operatingHours: { mode, timezone: base.operatingHours.timezone, schedule } });
			setSavedMode(mode);
			setSavedSchedule(schedule);
			toast.success('Operating hours saved');
		} catch (error) {
			console.error('[OperatingHoursTab.handleSave] failed to save operating hours', { mode, error });
			toast.error('Failed to save operating hours. Please try again.');
		}
	};

	if (isError) {
		return (
			<SettingsBody>
				<SettingsError />
			</SettingsBody>
		);
	}

	return (
		<>
			<SettingsBody>
				{isLoading ? (
					<SettingsSection>
						<SettingsCardSkeleton rows={1} />
						<SettingsCardSkeleton rows={3} />
					</SettingsSection>
				) : (
					<SettingsSection>
						<SettingsCard className='p-3'>
							<ToggleRow
								title='Operate full-time'
								description='Turning this on will make Kim AI operational 24x7'
								action={<Segmented options={MODE_OPTIONS} value={mode} onChange={setMode} disabled={!canEdit} />}
							/>
						</SettingsCard>

						<SettingsCard>
							<ToggleRow
								title='Timezone'
								description={brandTimezone || 'Not set. Kim cannot keep a custom schedule until the brand has one.'}
								action={
									<Link to='/settings/brand' className='flex shrink-0 items-center gap-1 text-[12px] font-medium text-indigo-500 hover:underline'>
										{brandTimezone ? 'Change in My Brand' : 'Set in My Brand'}
										<SquareArrowOutUpRight className='size-3.5' />
									</Link>
								}
							/>
						</SettingsCard>

						{mode === 'custom' && (
							<SettingsCard className='gap-3'>
								<div className='flex flex-col gap-0.5'>
									<p className='text-[14px] font-medium leading-5 text-ink-strong'>Set operating hours</p>
									<p className='text-[12px] leading-4 text-gray-900'>Customise this according to the brand&apos;s needs</p>
								</div>
								<div className='h-px w-full bg-gray-300' />
								<div className='flex flex-col gap-1'>
									{schedule.map((day, idx) => (
										<div key={day.day} className='flex flex-col gap-1'>
											<DayRow schedule={day} onChange={handleDayChange} />
											{idx < schedule.length - 1 && <SettingsDivider />}
										</div>
									))}
								</div>
								<p className='rounded-[8px] bg-gray-100 px-3 py-2 text-[12px] leading-4 text-gray-800'>
									NOTE : AI will function solely during the specified operating schedule above.
								</p>
							</SettingsCard>
						)}
					</SettingsSection>
				)}
			</SettingsBody>

			<SaveBar
				visible={isDirty}
				saving={updateMutation.isPending}
				canSave={canEdit && !hasScheduleConflicts}
				onCancel={handleCancel}
				onSave={() => void handleSave()}
			/>
		</>
	);
}

// ============================================================================
// Ingestion › Inbound — skip tickets already assigned to a person
// ============================================================================

/**
 * ai_settings.skipHumanAssigned, a row inside the "Blocked senders" card. The switch saves on
 * confirm; the agents exempt from it save as they are added or removed.
 */
export function SkipHumanAssignedRow() {
	const { data: settings, isLoading } = useAiSettings();
	const updateMutation = useUpdateAiSettings();
	const { can } = usePermissions();
	const canEdit = can('edit', 'brand');
	const [pendingSkipHuman, setPendingSkipHuman] = useState<boolean | null>(null);
	const exceptEmails = settings?.skipHumanAssignedExceptEmails ?? [];

	const save = async (patch: Partial<AiSettings>, done: string): Promise<boolean> => {
		try {
			await updateMutation.mutateAsync({ ...buildDefaultAiSettings(settings), ...patch });
			toast.success(done);
			return true;
		} catch (error) {
			console.error('[SkipHumanAssignedRow.save] failed to save', { error });
			toast.error('Failed to save. Please try again.');
			return false;
		}
	};

	const confirmSkipHuman = async () => {
		if (pendingSkipHuman === null) return;
		await save(
			{ skipHumanAssigned: pendingSkipHuman },
			pendingSkipHuman ? 'Tickets assigned to a person will be skipped' : 'Tickets assigned to a person will be processed'
		);
		setPendingSkipHuman(null);
	};

	const addExceptEmail = (raw: string): Promise<boolean> | boolean => {
		const email = raw.trim().toLowerCase();
		if (!EMAIL_RE.test(email) || email.length > 254) {
			toast.error(`"${email}" is not a valid email address.`);
			return false;
		}
		if (exceptEmails.includes(email)) {
			toast.info('Already in the list');
			return false;
		}
		if (exceptEmails.length >= MAX_LIST_ENTRIES) {
			toast.error(`You can add at most ${MAX_LIST_ENTRIES} emails.`);
			return false;
		}
		return save({ skipHumanAssignedExceptEmails: [...exceptEmails, email] }, `Kim will still process tickets assigned to ${email}`);
	};

	const removeExceptEmail = (email: string) =>
		void save({ skipHumanAssignedExceptEmails: exceptEmails.filter((e) => e !== email) }, `Removed ${email}`);

	return (
		<>
			<SettingsDivider />
			<ToggleRow
				title='Ignore tickets assigned to a person'
				description='When on, a ticket already assigned to one of your agents is left to them. Kim does not process, tag, assign, auto-reply or draft on it. Tickets assigned to the Kim user still run.'
				checked={settings?.skipHumanAssigned ?? false}
				disabled={!canEdit || isLoading || updateMutation.isPending}
				onCheckedChange={setPendingSkipHuman}
			/>
			{settings?.skipHumanAssigned && (
				<div className='flex flex-col gap-3 rounded-2xl border border-gray-300 bg-gray-50 p-4'>
					<ToggleRow
						title='Still process tickets assigned to'
						description='Agent emails whose tickets Kim keeps processing even while it skips tickets assigned to a person.'
					/>
					<ChipInput
						values={exceptEmails}
						onAdd={addExceptEmail}
						onRemove={removeExceptEmail}
						placeholder='agent@company.com'
						disabled={!canEdit || updateMutation.isPending}
						aria-label='Agent emails Kim still processes'
						withAddButton
					/>
				</div>
			)}
			<ConfirmToggleDialog
				pendingValue={pendingSkipHuman}
				icon={pendingSkipHuman === false ? Ban : UserX}
				title={pendingSkipHuman === false ? 'Process tickets assigned to a person?' : 'Ignore tickets assigned to a person?'}
				description={
					pendingSkipHuman === false ? (
						<p>
							Kim will process tickets even when one of your agents already holds them in the helpdesk: classifying, tagging,
							assigning, auto-replying and drafting as usual.
						</p>
					) : (
						<p>
							When a ticket arrives already assigned to one of your agents in the helpdesk, Kim leaves it entirely to them — no
							processing, tags, assignment, auto-reply or AI draft. Tickets assigned to the Kim user (or Sentinel&apos;s agent)
							still run.
						</p>
					)
				}
				confirmLabel={pendingSkipHuman === false ? 'Process them' : 'Ignore them'}
				isPending={updateMutation.isPending}
				onConfirm={() => void confirmSkipHuman()}
				onCancel={() => setPendingSkipHuman(null)}
			/>
		</>
	);
}

// ============================================================================
// Ingestion › Tags
// ============================================================================

const TAG_LISTS = [
	{
		key: 'processTags',
		other: 'ignoreTags',
		title: 'Process these tags',
		description: 'Tickets that carry any of these tags are eligible for Kim to respond to. Leave empty to process every tag.'
	},
	{
		key: 'ignoreTags',
		other: 'processTags',
		title: 'Ignore these tags',
		description: 'Tickets that carry any of these tags are skipped and left for the team.'
	}
] as const;

type TagLists = Record<(typeof TAG_LISTS)[number]['key'], string[]>;

export function TagFilteringTab() {
	const { data: settings, isLoading, isError } = useAiSettings();
	const updateMutation = useUpdateAiSettings();
	const { can } = usePermissions();
	const canEdit = can('edit', 'brand');

	const [tags, setTags] = useState<TagLists>({ processTags: [], ignoreTags: [] });
	const [savedTags, setSavedTags] = useState<TagLists>({ processTags: [], ignoreTags: [] });

	useEffect(() => {
		const loaded = { processTags: settings?.processTags ?? [], ignoreTags: settings?.ignoreTags ?? [] };
		setTags(loaded);
		setSavedTags(loaded);
	}, [settings]);

	const isDirty = JSON.stringify(tags) !== JSON.stringify(savedTags);

	const setList = (key: keyof TagLists, other: keyof TagLists, next: string[]): boolean => {
		const cleaned = cleanTagList(next, tags[other]);
		if (!cleaned) return false;
		setTags((prev) => ({ ...prev, [key]: cleaned }));
		return true;
	};

	const handleSave = async () => {
		try {
			await updateMutation.mutateAsync({ ...buildDefaultAiSettings(settings), ...tags });
			setSavedTags(tags);
			toast.success('Tag filters saved');
		} catch {
			toast.error('Failed to save tag filters. Please try again.');
		}
	};

	return (
		<>
			<SettingsBody>
				{isError ? (
					<SettingsError />
				) : (
					<SettingsSection label='Tag-based filtering'>
						{TAG_LISTS.map(({ key, other, title, description }) =>
							isLoading ? (
								<SettingsCardSkeleton key={key} rows={1} />
							) : (
								<SettingsCard key={key}>
									<ToggleRow title={title} description={description} />
									<ChipInput
										values={tags[key]}
										onAdd={(value) => setList(key, other, [...tags[key], value])}
										onRemove={(value) => setList(key, other, tags[key].filter((t) => t !== value))}
										placeholder='Type a tag and press Enter'
										aria-label={title}
										disabled={!canEdit}
									/>
								</SettingsCard>
							)
						)}
					</SettingsSection>
				)}
			</SettingsBody>
			<SaveBar
				visible={isDirty}
				saving={updateMutation.isPending}
				canSave={canEdit}
				onCancel={() => setTags(savedTags)}
				onSave={() => void handleSave()}
			/>
		</>
	);
}

// ============================================================================
// Ingestion › Channels
// ============================================================================

const CHANNEL_COPY: Record<string, { label: string; description: string }> = {
	email: { label: 'Email', description: 'Kim responds on email tickets.' },
	chat: { label: 'Chat', description: 'All-or-none. Kim responds on every chat conversation.' },
	sms: { label: 'SMS', description: 'All-or-none. Kim responds on every SMS conversation.' },
	social: { label: 'Social', description: 'All-or-none. Kim responds on social DMs and comments.' }
};

/** Figma's order; a channel the backend adds later goes last. */
const CHANNEL_ORDER = Object.keys(CHANNEL_COPY);
const channelRank = (name: string) => (CHANNEL_ORDER.includes(name) ? CHANNEL_ORDER.indexOf(name) : CHANNEL_ORDER.length);

const channelCopy = (name: string) =>
	CHANNEL_COPY[name] ?? {
		label: name.charAt(0).toUpperCase() + name.slice(1),
		description: `All-or-none. Kim responds on every ${name} conversation.`
	};

const FILTER_MODE_LABELS: Record<ChannelFilterMode, string> = { all: 'All addresses', specific: 'Specific addresses' };

interface ChannelFilterState {
	filterEnabled: boolean;
	filterMode: ChannelFilterMode;
	inboxes: ChannelInboxEntry[];
}

type FilterMap = Record<string, ChannelFilterState>;

const DEFAULT_FILTER_STATE: ChannelFilterState = { filterEnabled: false, filterMode: 'all', inboxes: [] };

function toFilterMap(channels: Channel[]): FilterMap {
	const map: FilterMap = {};
	for (const ch of channels) {
		map[ch.name] = {
			filterEnabled: ch.metadata?.filterEnabled ?? false,
			filterMode: ch.metadata?.filterMode ?? 'all',
			inboxes: ch.metadata?.inboxes ?? []
		};
	}
	return map;
}

interface EmailAddressesProps {
	state: ChannelFilterState;
	canEdit: boolean;
	onModeChange: (mode: ChannelFilterMode) => void;
	onAdd: (value: string) => void;
	onToggle: (id: string, enabled: boolean) => void;
	onDelete: (id: string) => void;
}

/** Email only: reply on every inbox, or only on the listed addresses (each can be paused). */
function EmailAddresses({ state, canEdit, onModeChange, onAdd, onToggle, onDelete }: EmailAddressesProps) {
	const [draft, setDraft] = useState('');

	const add = () => {
		const value = draft.trim();
		if (!value) return;
		if (!EMAIL_RE.test(value)) {
			toast.error('Please enter a valid email address.');
			return;
		}
		onAdd(value);
		setDraft('');
	};

	return (
		<div className='flex flex-col gap-3 rounded-2xl border border-gray-500 bg-gray-100 p-3'>
			<div className='flex items-center gap-4'>
				<div className='flex min-w-0 flex-1 flex-col gap-0.5'>
					<p className='text-[13px] font-medium leading-[18px] text-ink'>Respond on</p>
					<p className='text-[12px] leading-4 text-gray-900'>
						{state.filterMode === 'specific' ? 'Kim replies only to email sent to these addresses.' : 'Kim replies to email sent to any of your inboxes.'}
					</p>
				</div>
				<SettingsSelect
					value={state.filterMode}
					onValueChange={(value) => onModeChange(value as ChannelFilterMode)}
					options={(Object.keys(FILTER_MODE_LABELS) as ChannelFilterMode[]).map((mode) => ({ value: mode, label: FILTER_MODE_LABELS[mode] }))}
					disabled={!canEdit}
					aria-label='Respond on'
					className='w-[200px]'
				/>
			</div>

			{state.filterMode === 'specific' && (
				<>
					{state.inboxes.length > 0 && (
						<ul className='overflow-hidden rounded-[10px] border border-gray-500 bg-white'>
							{state.inboxes.map((entry) => (
								<li key={entry.id} className='flex items-center gap-2.5 border-b border-gray-300 py-2 pl-3 pr-2 last:border-b-0'>
									<Mail className='size-4 shrink-0 text-gray-800' />
									<span className={cn('min-w-0 flex-1 truncate text-[13px] leading-[18px]', entry.enabled ? 'text-ink' : 'text-gray-700')}>
										{entry.value}
									</span>
									<Switch
										checked={entry.enabled}
										disabled={!canEdit}
										aria-label={`Respond on ${entry.value}`}
										onCheckedChange={(checked) => onToggle(entry.id, checked)}
									/>
									<button
										type='button'
										onClick={() => onDelete(entry.id)}
										disabled={!canEdit}
										aria-label={`Remove ${entry.value}`}
										className={cn(ICON_BUTTON_CLASS, 'disabled:cursor-not-allowed disabled:opacity-50')}
									>
										<Trash2 className='size-3.5' />
									</button>
								</li>
							))}
						</ul>
					)}
					<div className='flex items-center gap-2'>
						<input
							type='email'
							value={draft}
							onChange={(e) => setDraft(e.target.value)}
							onKeyDown={(e) => {
								if (e.key !== 'Enter') return;
								e.preventDefault();
								add();
							}}
							disabled={!canEdit}
							placeholder='name@company.com'
							aria-label='Email address'
							className={SETTINGS_INPUT_CLASS}
						/>
						<button type='button' onClick={add} disabled={!canEdit || !draft.trim()} className={SETTINGS_PILL_BUTTON_CLASS}>
							<Plus className='size-3.5' />
							Add address
						</button>
					</div>
				</>
			)}
		</div>
	);
}

export function ChannelFilteringTab() {
	const { data: channelsData, isLoading, isError } = useDefaultChannels();
	const updateChannel = useUpdateChannel();
	const { can } = usePermissions();
	const canEdit = can('edit', 'brand');

	const channels = [...((channelsData as Channel[] | undefined) ?? [])].sort((a, b) => channelRank(a.name) - channelRank(b.name));

	const [filterMap, setFilterMap] = useState<FilterMap>({});
	const [savedFilterMap, setSavedFilterMap] = useState<FilterMap>({});

	useEffect(() => {
		const initial = toFilterMap(channels);
		setFilterMap(initial);
		setSavedFilterMap(initial);
	}, [channelsData]); // eslint-disable-line react-hooks/exhaustive-deps

	const isDirty = JSON.stringify(filterMap) !== JSON.stringify(savedFilterMap);

	const patchChannel = (name: string, update: (cur: ChannelFilterState) => Partial<ChannelFilterState>) =>
		setFilterMap((prev) => {
			const cur = prev[name] ?? DEFAULT_FILTER_STATE;
			return { ...prev, [name]: { ...cur, ...update(cur) } };
		});

	const handleSave = async () => {
		const dirtyChannels = channels.filter((ch) => JSON.stringify(filterMap[ch.name]) !== JSON.stringify(savedFilterMap[ch.name]));
		try {
			await Promise.all(
				dirtyChannels.map((ch) => {
					const s = filterMap[ch.name] ?? DEFAULT_FILTER_STATE;
					return updateChannel.mutateAsync({
						id: ch.id,
						data: { metadata: { inboxes: s.inboxes, filterEnabled: s.filterEnabled, filterMode: s.filterMode } }
					});
				})
			);
			setSavedFilterMap(filterMap);
			toast.success('Channel filters saved');
		} catch {
			toast.error('Failed to save channel filters. Please try again.');
		}
	};

	return (
		<>
			<SettingsBody>
				{isError ? (
					<SettingsError>Failed to load channels. Please try again later.</SettingsError>
				) : (
					<SettingsSection label='Channels Kim responds on'>
						{isLoading ? (
							<SettingsCardSkeleton rows={4} />
						) : (
							<SettingsCard>
								{channels.map((ch, idx) => {
									const state = filterMap[ch.name] ?? DEFAULT_FILTER_STATE;
									const { label, description } = channelCopy(ch.name);
									return (
										<div key={ch.name} className='flex flex-col gap-4'>
											{idx > 0 && <SettingsDivider />}
											<ToggleRow
												title={label}
												description={description}
												checked={state.filterEnabled}
												disabled={!canEdit}
												onCheckedChange={(filterEnabled) => patchChannel(ch.name, () => ({ filterEnabled }))}
											/>
											{/* Only email supports per-address matching; the other channels are all-or-none. */}
											{ch.name === 'email' && state.filterEnabled && (
												<EmailAddresses
													state={state}
													canEdit={canEdit}
													onModeChange={(filterMode) => patchChannel(ch.name, () => ({ filterMode }))}
													onAdd={(value) =>
														patchChannel(ch.name, (cur) => ({
															inboxes: [...cur.inboxes, { id: crypto.randomUUID(), value, enabled: true }]
														}))
													}
													onToggle={(id, enabled) =>
														patchChannel(ch.name, (cur) => ({ inboxes: cur.inboxes.map((e) => (e.id === id ? { ...e, enabled } : e)) }))
													}
													onDelete={(id) => patchChannel(ch.name, (cur) => ({ inboxes: cur.inboxes.filter((e) => e.id !== id) }))}
												/>
											)}
										</div>
									);
								})}
							</SettingsCard>
						)}
						<p className='text-[12px] leading-4 text-gray-900'>Disabled channels are skipped and passed to your team untouched.</p>
					</SettingsSection>
				)}
			</SettingsBody>
			<SaveBar
				visible={isDirty}
				saving={updateChannel.isPending}
				canSave={canEdit}
				onCancel={() => setFilterMap(savedFilterMap)}
				onSave={() => void handleSave()}
			/>
		</>
	);
}
